// frontend/src/pages/Consumables/index.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Tabs, Table, Button, Input, Select, Space, Tag, Modal, Form,
  InputNumber, Tooltip, Alert, DatePicker, Segmented, Typography,
  Row, Col, Statistic, message, Card,
} from 'antd';
import {
  PlusOutlined, MinusOutlined, SearchOutlined, WarningOutlined,
  BarChartOutlined, TableOutlined, ReloadOutlined, AppstoreOutlined,
  ShopOutlined, DatabaseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { consumablesAPI, dictAPI, employeesAPI } from '../../api/client';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import TableToolbar from '../../components/TableToolbar';
import ExportButton from '../../components/ExportButton';
import useTableSort from '../../hooks/useTableSort';
import { sortTableRows } from '../../utils/tableRows';
import { fetchServerTableData, makeTablePagination, normalizeTablePagination } from '../../utils/tablePagination';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const ISSUE_REASONS = [
  { value: 'new_employee',  label: 'Новый сотрудник' },
  { value: 'replacement',   label: 'Замена' },
  { value: 'additional',    label: 'Доп. оборудование' },
];
const reasonLabel = (r) => ISSUE_REASONS.find(x => x.value === r)?.label || '—';

// ─── Recharts-графики ───────────────────────────────────────────

const CHART_COLORS = ['#4096ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2'];

function Top10Chart({ data }) {
  if (!data?.length) return <Text type="secondary">Нет данных</Text>;
  const chartData = [...data].reverse().map(d => ({ name: d.name, value: Number(d.total_issued) }));
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical" margin={{ left: 16, right: 32, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 12 }} />
        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
        <ReTooltip formatter={(v) => [v, 'Выдано']} />
        <Bar dataKey="value" fill="#4096ff" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function MonthlyChart({ monthly }) {
  if (!monthly?.length) return <Text type="secondary">Нет данных</Text>;
  const months = [...new Set(monthly.map(r => r.month))].sort();
  const byMonth = {};
  monthly.forEach(r => {
    if (!byMonth[r.month]) byMonth[r.month] = {};
    byMonth[r.month][r.action] = Number(r.total);
  });
  const data = months.map(m => ({
    month: m.slice(0, 7),
    Приход:   byMonth[m]['Приход']   || 0,
    Выдача:   byMonth[m]['Выдача']   || 0,
    Возврат:  byMonth[m]['Возврат']  || 0,
    Списание: byMonth[m]['Списание'] || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <ReTooltip />
        <Legend />
        <Bar dataKey="Приход"   fill="#52c41a" radius={[2,2,0,0]} />
        <Bar dataKey="Выдача"   fill="#4096ff" radius={[2,2,0,0]} />
        <Bar dataKey="Возврат"  fill="#faad14" radius={[2,2,0,0]} />
        <Bar dataKey="Списание" fill="#ff4d4f" radius={[2,2,0,0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ReasonsChart({ data }) {
  if (!data?.length) return <Text type="secondary">Нет данных</Text>;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return <Text type="secondary">Нет данных</Text>;
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={13}>{`${(percent * 100).toFixed(0)}%`}</text>;
  };
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%"
          outerRadius={100} labelLine={false} label={renderLabel}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <ReTooltip formatter={(v, name) => [v, name]} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ─── Вкладка Остатки ────────────────────────────────────────────

function StockTab() {
  const [stock, setStock]           = useState([]);
  const [tableStock, setTableStock] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [classification, setClassification] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [selectedRows, setSelected] = useState([]);
  const [modalType, setModalType]   = useState(null); // 'income' | 'issue'
  const [form]                      = Form.useForm();
  const [employees, setEmployees]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems]           = useState([]);
  const [editMinQty, setEditMinQty] = useState({});
  // Склад выбранный в модалке (единый для всех строк)
  const [modalLocation, setModalLocation] = useState(null);
  // Тип позиции для прихода: 'existing' | 'new'
  const [posType, setPosType]       = useState('existing');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'name',
    initialSortDir: 'asc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });
  const stockQueryParams = useMemo(() => ({
    search: search.trim() || undefined,
    classification: classification || undefined,
    category: categoryFilter || undefined,
    location: locationFilter || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  }), [search, classification, categoryFilter, locationFilter, sortBy, sortDir]);

  const loadStock = useCallback(async () => {
    const res = await consumablesAPI.getStock();
    setStock(res.data);
  }, []);

  const loadTableStock = useCallback(async () => {
    setLoading(true);
    try {
      const res = await consumablesAPI.getStock(stockQueryParams);
      setTableStock(res.data);
    } finally { setLoading(false); }
  }, [stockQueryParams]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [stockRes, tableRes] = await Promise.all([
        consumablesAPI.getStock(),
        consumablesAPI.getStock(stockQueryParams),
      ]);
      setStock(stockRes.data);
      setTableStock(tableRes.data);
    } finally { setLoading(false); }
  }, [stockQueryParams]);

  useEffect(() => { loadStock(); }, [loadStock]);
  useEffect(() => { loadTableStock(); }, [loadTableStock]);

  useEffect(() => {
    employeesAPI.getAll({ limit: 999 }).then(r => setEmployees(r.data?.items || r.data || []));
    consumablesAPI.getCategories().then(r => setCategories(r.data));
    consumablesAPI.getItems().then(r => setItems(r.data));
  }, []);

  // Все уникальные склады из остатков
  const allLocations = [...new Map(stock.map(s => [s.location_id, { label: s.location, value: s.location_id }])).values()];

  const openModal = (type) => {
    setModalType(type);
    setModalLocation(null);
    setPosType('existing');
    form.resetFields();
    form.setFieldValue('rows', [{}]);
  };

  // Позиции доступные на выбранном складе (для выдачи — только с остатком > 0)
  const availableItems = items.filter(it => {
    if (!modalLocation) return modalType !== 'issue'; // для выдачи без склада — ничего
    if (modalType === 'issue') {
      return stock.some(s => s.consumable_id === it.id && s.location_id === modalLocation && s.quantity > 0);
    }
    return true; // для прихода — все позиции справочника
  });

  const handleSubmit = async () => {
    const vals = await form.validateFields();
    const action = modalType === 'income' ? 'Приход' : 'Выдача';

    if (!modalLocation) return message.error('Выберите склад');

    let processedRows = [];

    if (modalType === 'income' && posType === 'new') {
      // Новая позиция: создаём в справочнике, потом приход
      const { newName, newCategoryId, newUnit, newQty } = vals;
      if (!newName || !newCategoryId || !newQty) return message.error('Заполните все поля новой позиции');
      const r = await consumablesAPI.createItem({ name: newName.trim(), category_id: newCategoryId, unit: newUnit || 'шт' });
      processedRows = [{ consumable_id: r.data.id, location_id: modalLocation, quantity: newQty }];
    } else {
      // Существующие позиции
      const rows = (vals.rows || []).filter(r => r?.consumable_id && r?.quantity);
      if (!rows.length) return message.error('Заполните хотя бы одну строку');
      processedRows = rows.map(r => ({ consumable_id: r.consumable_id, location_id: modalLocation, quantity: r.quantity }));
    }

    await consumablesAPI.addBulkMovement({
      action,
      items: processedRows,
      employee_id:  vals.employee_id || null,
      notes:        vals.notes || null,
      issue_reason: modalType === 'issue' ? vals.issue_reason : null,
      created_by:   'web',
    });
    message.success(`${action} записана`);
    setModalType(null);
    refresh();
  };

  const saveMinQty = async (consumable_id, val) => {
    await consumablesAPI.updateItem(consumable_id, { min_quantity: val });
    setEditMinQty(p => ({ ...p, [consumable_id]: undefined }));
    refresh();
  };

  const classificationOptions = [...new Set(stock.map(s => s.classification).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'ru'))
    .map(v => ({ label: v, value: v }));
  const categoryOptions = [...new Set(stock.map(s => s.category).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'ru'))
    .map(v => ({ label: v, value: v }));
  const locationOptions = [...new Set(stock.map(s => s.location).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'ru'))
    .map(l => ({ label: l, value: l }));

  const hasFilters = Boolean(search || classification || categoryFilter || locationFilter);
  const resetFilters = () => {
    setSearch('');
    setClassification(null);
    setCategoryFilter(null);
    setLocationFilter(null);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const columns = [
    { title: `Классификация${sortIcon('classification')}`, dataIndex: 'classification', key: 'cls', width: 140,
      onHeaderCell: () => getSortableHeaderProps('classification') },
    { title: `Категория${sortIcon('category')}`, dataIndex: 'category', key: 'cat', width: 150,
      onHeaderCell: () => getSortableHeaderProps('category') },
    { title: `Наименование${sortIcon('name')}`, dataIndex: 'name', key: 'name', width: 280, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('name'),
      render: (v, r) => (
        <Space>
          {r.min_quantity > 0 && r.quantity <= r.min_quantity && (
            <Tooltip title={`Остаток ≤ минимума (${r.min_quantity})`}>
              <WarningOutlined style={{ color: '#ff4d4f' }} />
            </Tooltip>
          )}
          <Text strong>{v}</Text>
        </Space>
      )},
    { title: `Склад${sortIcon('location')}`, dataIndex: 'location', key: 'loc', width: 150,
      onHeaderCell: () => getSortableHeaderProps('location') },
    { title: `Остаток${sortIcon('quantity')}`, dataIndex: 'quantity', key: 'qty', width: 100, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('quantity'),
      render: (v, r) => (
        <Text style={{ color: r.min_quantity > 0 && v <= r.min_quantity ? '#ff4d4f' : undefined }} strong>
          {v} {r.unit}
        </Text>
      )},
    { title: `Мин. остаток${sortIcon('min_quantity')}`, key: 'minqty', width: 130, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('min_quantity'),
      render: (_, r) => {
        const editing = editMinQty[r.consumable_id] !== undefined;
        return editing ? (
          <Space.Compact size="small">
            <InputNumber
              size="small" min={0} defaultValue={r.min_quantity}
              onChange={v => setEditMinQty(p => ({ ...p, [r.consumable_id]: v }))}
              style={{ width: 60 }}
            />
            <Button size="small" type="primary"
              onClick={() => saveMinQty(r.consumable_id, editMinQty[r.consumable_id] ?? r.min_quantity)}>
              ✓
            </Button>
          </Space.Compact>
        ) : (
          <div style={{ cursor: 'pointer' }} onClick={() => setEditMinQty(p => ({ ...p, [r.consumable_id]: r.min_quantity }))}>
            {r.min_quantity || <Text type="secondary">—</Text>}
          </div>
        );
      }},
  ];

  return (
    <div>
      <TableToolbar>
        <Space wrap size={8}>
          <Input placeholder="Поиск по наименованию или категории" prefix={<SearchOutlined />} allowClear
            style={{ width: 320 }} value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }} />
          <Select placeholder="Классификация" allowClear style={{ width: 170 }} value={classification}
            onChange={value => { setClassification(value); setPagination(p => ({ ...p, current: 1 })); }} options={classificationOptions} />
          <Select placeholder="Категория" allowClear showSearch optionFilterProp="label" style={{ width: 190 }}
            value={categoryFilter} onChange={value => { setCategoryFilter(value); setPagination(p => ({ ...p, current: 1 })); }} options={categoryOptions} />
          <Select placeholder="Склад" allowClear showSearch optionFilterProp="label" style={{ width: 180 }}
            value={locationFilter} onChange={value => { setLocationFilter(value); setPagination(p => ({ ...p, current: 1 })); }} options={locationOptions} />
          {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
          <ExportButton
            onExport={(fileFormat) => consumablesAPI.exportStock(stockQueryParams, fileFormat)}
            filenameBase="consumables-stock"
          />
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Обновить</Button>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => openModal('income')}>Приход</Button>
          <Button icon={<MinusOutlined />} onClick={() => openModal('issue')}>Выдача</Button>
        </Space>
      </TableToolbar>

      {selectedRows.length > 0 && (
        <Alert style={{ marginBottom: 8 }}
          message={
            <Space>
              <Text>Выбрано: {selectedRows.length}</Text>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openModal('income')}>Приход</Button>
              <Button size="small" icon={<MinusOutlined />} onClick={() => openModal('issue')}>Выдача</Button>
            </Space>
          } />
      )}

      <Card size="small">
        <Table
          dataSource={tableStock} columns={columns} loading={loading}
          rowKey="stock_id" size="small" scroll={{ x: 900 }}
          rowSelection={{ selectedRowKeys: selectedRows.map(r => r.stock_id), onChange: (_, rows) => setSelected(rows) }}
          pagination={makeTablePagination({
            ...pagination,
            total: tableStock.length,
            showTotal: t => `Всего ${t} позиций`,
          })}
          onChange={(pag) => setPagination(normalizeTablePagination(pag))}
        />
      </Card>

      <Modal
        open={!!modalType}
        title={
          <Space>
            <Tag color={modalType === 'income' ? 'green' : 'blue'} style={{ marginRight: 0 }}>
              {modalType === 'income' ? 'Приход' : 'Выдача'}
            </Tag>
            <span>расходников</span>
          </Space>
        }
        onCancel={() => setModalType(null)} onOk={handleSubmit}
        okText="Записать" width={680} destroyOnClose
      >
        <Form form={form} layout="vertical">

          {/* Склад — общий для всех строк */}
          <Form.Item label={<Text strong>Склад</Text>} required style={{ marginBottom: 16 }}>
            <Select
              placeholder="Выберите склад"
              value={modalLocation}
              onChange={v => { setModalLocation(v); form.setFieldValue('rows', [{}]); }}
              options={allLocations}
              style={{ width: '100%' }}
              showSearch optionFilterProp="label"
            />
          </Form.Item>

          {/* Выдача: сотрудник и причина */}
          {modalType === 'issue' && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="employee_id" label="Сотрудник">
                  <Select showSearch optionFilterProp="label" placeholder="Кому выдаём" allowClear
                    options={(employees?.items || employees).map(e => ({ label: e.full_name, value: e.id }))} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="issue_reason" label="Причина выдачи">
                  <Select placeholder="Выберите причину" allowClear options={ISSUE_REASONS} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Приход: переключатель тип позиции */}
          {modalType === 'income' && (
            <Form.Item label="Тип позиции">
              <Segmented
                value={posType} onChange={setPosType}
                options={[
                  { label: 'Существующая позиция', value: 'existing' },
                  { label: 'Новая позиция', value: 'new' },
                ]}
              />
            </Form.Item>
          )}

          {/* Новая позиция */}
          {modalType === 'income' && posType === 'new' && (
            <div style={{ background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <Text strong style={{ color: '#722ed1' }}>Новая позиция в справочнике</Text>
              <Row gutter={12} style={{ marginTop: 12 }}>
                <Col span={10}>
                  <Form.Item name="newName" label="Название" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                    <Input placeholder="Например: Logitech MX Keys" />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item name="newCategoryId" label="Категория" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                    <Select showSearch optionFilterProp="label" placeholder="Выберите"
                      options={categories.map(c => ({ label: `${c.classification} / ${c.name}`, value: c.id }))} />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item name="newUnit" label="Ед." initialValue="шт" style={{ marginBottom: 8 }}>
                    <Input placeholder="шт" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="newQty" label="Количество" rules={[{ required: true }]} style={{ marginBottom: 0, maxWidth: 140 }}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          )}

          {/* Существующие позиции (мультистрочно) */}
          {!(modalType === 'income' && posType === 'new') && (
            <>
              {!modalLocation && modalType === 'issue' && (
                <Alert type="warning" message="Сначала выберите склад — список позиций фильтруется по нему" style={{ marginBottom: 8 }} showIcon />
              )}
              <Form.List name="rows" initialValue={[{}]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <Space key={key} align="start" style={{ display: 'flex', marginBottom: 8 }} wrap>
                        <Form.Item name={[name, 'consumable_id']} style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: 'Выберите позицию' }]}>
                          <Select showSearch optionFilterProp="label" placeholder="Выберите позицию"
                            style={{ width: 360 }}
                            options={availableItems.map(it => ({
                              label: `${it.name} (${it.category})`,
                              value: it.id,
                            }))}
                          />
                        </Form.Item>
                        <Form.Item name={[name, 'quantity']} style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: 'Кол-во' }]}>
                          <InputNumber min={1} placeholder="Кол-во" style={{ width: 100 }} />
                        </Form.Item>
                        {fields.length > 1 && (
                          <Button danger size="small" onClick={() => remove(name)}>✕</Button>
                        )}
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>Добавить позицию</Button>
                  </>
                )}
              </Form.List>
            </>
          )}

          <Form.Item name="notes" label="Комментарий" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} placeholder="Необязательно" />
          </Form.Item>

        </Form>
      </Modal>
    </div>
  );
}

// ─── Вкладка Журнал ─────────────────────────────────────────────

function LogTab() {
  const [log, setLog]       = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'created_at',
    initialSortDir: 'desc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });
  const { current: currentPage, pageSize } = pagination;
  const queryParams = useMemo(() => ({
    search: search || undefined,
    action: action || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  }), [search, action, sortBy, sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchServerTableData(
        params => consumablesAPI.getLog(params).then(r => r.data),
        queryParams,
        { current: currentPage, pageSize },
      );
      setLog(data);
    } finally { setLoading(false); }
  }, [queryParams, currentPage, pageSize]);

  useEffect(() => { load(); }, [load]);

  const actionColors = { Приход: 'green', Выдача: 'blue', Возврат: 'orange', Списание: 'red' };
  const hasFilters = Boolean(search || action);
  const resetFilters = () => {
    setSearch('');
    setAction('');
    setPagination(p => ({ ...p, current: 1 }));
  };

  const columns = [
    { title: `Дата${sortIcon('created_at')}`, dataIndex: 'created_at', key: 'date', width: 150,
      onHeaderCell: () => getSortableHeaderProps('created_at'),
      render: v => dayjs(v).format('DD.MM.YYYY HH:mm') },
    { title: `Действие${sortIcon('action')}`, dataIndex: 'action', key: 'action', width: 120,
      onHeaderCell: () => getSortableHeaderProps('action'),
      render: v => <Tag color={actionColors[v]}>{v}</Tag> },
    { title: `Категория${sortIcon('category')}`, dataIndex: 'category', key: 'cat', width: 140, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('category') },
    { title: `Наименование${sortIcon('name')}`, dataIndex: 'name', key: 'name', ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('name') },
    { title: `Склад${sortIcon('location')}`, dataIndex: 'location', key: 'loc', width: 140, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('location') },
    { title: `Кол-во${sortIcon('quantity')}`, dataIndex: 'quantity', key: 'qty', width: 90, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('quantity') },
    { title: `Сотрудник${sortIcon('employee')}`, dataIndex: 'employee', key: 'emp', width: 170, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('employee'),
      render: v => v || '—' },
    { title: `Причина${sortIcon('issue_reason')}`, dataIndex: 'issue_reason', key: 'reason', width: 150,
      onHeaderCell: () => getSortableHeaderProps('issue_reason'),
      render: v => v ? <Tag>{reasonLabel(v)}</Tag> : '—' },
    { title: 'Комментарий', dataIndex: 'notes', key: 'notes', ellipsis: true, render: v => v || '—' },
  ];

  return (
    <div>
      <TableToolbar>
        <Space wrap size={8}>
          <Input placeholder="Поиск по наименованию или категории" prefix={<SearchOutlined />} allowClear
            style={{ width: 320 }} value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }} />
          <Select placeholder="Действие" allowClear style={{ width: 160 }} value={action || undefined}
            onChange={v => { setAction(v || ''); setPagination(p => ({ ...p, current: 1 })); }}
            options={['Приход', 'Выдача', 'Возврат', 'Списание'].map(v => ({ label: v, value: v }))} />
          {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
          <ExportButton
            onExport={(fileFormat) => consumablesAPI.exportLog(queryParams, fileFormat)}
            filenameBase="consumables-log"
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Обновить</Button>
        </Space>
      </TableToolbar>
      <Card size="small">
        <Table
          dataSource={log.items} columns={columns} loading={loading}
          rowKey="id" size="small" scroll={{ x: 1100 }}
          pagination={makeTablePagination({
            ...pagination,
            total: log.total,
            showTotal: t => `Всего ${t} записей`,
          })}
          onChange={(pag) => setPagination(normalizeTablePagination(pag))}
        />
      </Card>
    </div>
  );
}

// ─── Вкладка Аналитика ──────────────────────────────────────────

function AnalyticsTab() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [dateRange, setDateRange] = useState([dayjs().subtract(3, 'month'), dayjs()]);
  const [categoryId, setCategoryId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [issueReason, setIssueReason] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations]   = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'name',
    initialSortDir: 'asc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });

  useEffect(() => {
    consumablesAPI.getCategories().then(r => setCategories(r.data || [])).catch(() => {});
    dictAPI.getLocations().then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.date_to   = dateRange[1].format('YYYY-MM-DD');
      if (categoryId)     params.category_id = categoryId;
      if (locationId)     params.location_id = locationId;
      if (issueReason)    params.issue_reason = issueReason;
      const res = await consumablesAPI.getAnalytics(params);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Ошибка загрузки аналитики');
    } finally { setLoading(false); }
  }, [dateRange, categoryId, locationId, issueReason]);

  useEffect(() => { load(); }, [load]);

  const summaryRows = useMemo(() => sortTableRows(data?.summary || [], {
    sortBy,
    sortDir,
    types: {
      income: 'number',
      issue: 'number',
      ret: 'number',
      writeoff: 'number',
    },
  }), [data, sortBy, sortDir]);

  const resetFilters = () => {
    setDateRange([dayjs().subtract(3, 'month'), dayjs()]);
    setCategoryId(null);
    setLocationId(null);
    setIssueReason(null);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const summaryColumns = [
    { title: `Классификация${sortIcon('classification')}`, dataIndex: 'classification', key: 'cls', width: 140,
      onHeaderCell: () => getSortableHeaderProps('classification') },
    { title: `Категория${sortIcon('category')}`, dataIndex: 'category', key: 'cat', width: 150, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('category') },
    { title: `Наименование${sortIcon('name')}`, dataIndex: 'name', key: 'name', ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('name') },
    { title: 'Ед.',           dataIndex: 'unit',            key: 'unit', width: 60 },
    { title: `Приход${sortIcon('income')}`, dataIndex: 'income', key: 'inc', width: 100, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('income'),
      render: v => v > 0 ? <Text style={{ color: '#52c41a' }}>{v}</Text> : <Text type="secondary">0</Text> },
    { title: `Выдача${sortIcon('issue')}`, dataIndex: 'issue', key: 'iss', width: 100, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('issue'),
      render: v => v > 0 ? <Text style={{ color: '#4096ff' }}>{v}</Text> : <Text type="secondary">0</Text> },
    { title: `Возврат${sortIcon('ret')}`, dataIndex: 'ret', key: 'ret', width: 100, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('ret'),
      render: v => v > 0 ? <Text style={{ color: '#faad14' }}>{v}</Text> : <Text type="secondary">0</Text> },
    { title: `Списание${sortIcon('writeoff')}`, dataIndex: 'writeoff', key: 'wo', width: 110, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('writeoff'),
      render: v => v > 0 ? <Text style={{ color: '#ff4d4f' }}>{v}</Text> : <Text type="secondary">0</Text> },
  ];

  const lowStockColumns = [
    { title: 'Категория',    dataIndex: 'category',      key: 'cat', width: 150 },
    { title: 'Наименование', dataIndex: 'name',           key: 'name', ellipsis: true },
    { title: 'Склад',        dataIndex: 'location',       key: 'loc', width: 140 },
    { title: 'Остаток',      dataIndex: 'quantity',       key: 'qty', width: 90, align: 'center',
      render: (v, r) => <Text style={{ color: '#ff4d4f' }} strong>{v} {r.unit}</Text> },
    { title: 'Мин.',         dataIndex: 'min_quantity',   key: 'min', width: 80, align: 'center' },
  ];

  const pieData = data?.reasons?.map(r => ({
    label: r.reason === 'not_specified' ? 'Не указана' : reasonLabel(r.reason),
    value: Number(r.total),
  })) || [];

  const totalIssued = data?.summary?.reduce((s, r) => s + Number(r.issue || 0), 0) || 0;
  const totalIncome = data?.summary?.reduce((s, r) => s + Number(r.income || 0), 0) || 0;
  const hasAnalyticsFilters = Boolean(
    categoryId || locationId || issueReason ||
    !dateRange?.[0]?.isSame(dayjs().subtract(3, 'month'), 'day') ||
    !dateRange?.[1]?.isSame(dayjs(), 'day')
  );

  return (
    <div>
      <TableToolbar>
        <Space wrap size={8}>
          <RangePicker value={dateRange}
            onChange={value => { setDateRange(value); setPagination(p => ({ ...p, current: 1 })); }}
            format="DD.MM.YYYY"
            presets={[
              { label: '30 дней',   value: [dayjs().subtract(30, 'day'), dayjs()] },
              { label: '3 месяца',  value: [dayjs().subtract(3, 'month'), dayjs()] },
              { label: 'Полгода',   value: [dayjs().subtract(6, 'month'), dayjs()] },
              { label: 'Этот год',  value: [dayjs().startOf('year'), dayjs()] },
            ]} />
          <Select placeholder="Категория" allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
            value={categoryId} onChange={value => { setCategoryId(value); setPagination(p => ({ ...p, current: 1 })); }}
            options={categories.map(c => ({ label: `${c.classification} / ${c.name}`, value: c.id }))} />
          <Select placeholder="Склад" allowClear showSearch optionFilterProp="label" style={{ width: 180 }}
            value={locationId} onChange={value => { setLocationId(value); setPagination(p => ({ ...p, current: 1 })); }}
            options={(locations || []).map(l => ({ label: l.name, value: l.id }))} />
          <Select placeholder="Причина выдачи" allowClear style={{ width: 180 }} value={issueReason}
            onChange={value => { setIssueReason(value); setPagination(p => ({ ...p, current: 1 })); }} options={ISSUE_REASONS} />
          {hasAnalyticsFilters && <Button onClick={resetFilters}>Сбросить</Button>}
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Обновить</Button>
        </Space>
      </TableToolbar>

      {error && (
        <Alert type="error" message="Ошибка загрузки аналитики" description={error}
          style={{ marginBottom: 16 }} showIcon />
      )}

      {/* Карточки */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Приход" value={totalIncome} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Выдано" value={totalIssued} valueStyle={{ color: '#4096ff' }} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Позиций с низким остатком" value={data?.low_stock?.length || 0} valueStyle={{ color: '#faad14' }} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Уникальных позиций" value={data?.summary?.length || 0} /></Card>
        </Col>
      </Row>

      {/* Низкие остатки — всегда видны */}
      {data?.low_stock?.length > 0 && (
        <Alert
          type="warning" style={{ marginBottom: 16 }}
          message={<Text strong><WarningOutlined style={{ marginRight: 6 }} />Низкие остатки: {data.low_stock.length} позиций</Text>}
          description={
            <Table
              dataSource={data.low_stock} columns={lowStockColumns}
              rowKey={(r, i) => i} size="small" pagination={false}
              scroll={{ x: 700 }} style={{ marginTop: 8 }}
            />
          }
        />
      )}

      {/* Переключатель */}
      <Segmented
        value={viewMode} onChange={setViewMode}
        options={[
          { label: <Space><BarChartOutlined />Графики</Space>, value: 'charts' },
          { label: <Space><TableOutlined />Таблица</Space>,    value: 'table' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {viewMode === 'table' ? (
        <Card size="small">
          <Table
            dataSource={summaryRows} columns={summaryColumns}
            loading={loading} rowKey={(r, i) => i} size="small"
            scroll={{ x: 850 }}
            pagination={makeTablePagination({
              ...pagination,
              total: summaryRows.length,
              showTotal: t => `Всего позиций: ${t}`,
            })}
            onChange={(pag) => setPagination(normalizeTablePagination(pag))}
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card size="small" title="Динамика по месяцам">
              {data && <MonthlyChart monthly={data.monthly} />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="Топ-10 выдаваемых позиций">
              {data && <Top10Chart data={data.top10} />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="Причины выдачи">
              <ReasonsChart data={pieData} />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

// ─── Главный компонент ──────────────────────────────────────────

export default function ConsumablesPage() {
  const [stats, setStats] = useState({ total: 0, positions: 0, lowStock: 0, locations: 0 });

  useEffect(() => {
    consumablesAPI.getStock().then(res => {
      const stock = res.data || [];
      const totalQty    = stock.reduce((s, r) => s + Number(r.quantity || 0), 0);
      const positions   = new Set(stock.map(r => r.consumable_id)).size;
      const lowStock    = stock.filter(r => r.min_quantity > 0 && r.quantity <= r.min_quantity).length;
      const locations   = new Set(stock.map(r => r.location_id)).size;
      setStats({ total: totalQty, positions, lowStock, locations });
    });
  }, []);

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Заголовок */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Расходники</Title>
      </div>

      {/* Карточки статистики */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Всего единиц" value={stats.total} prefix={<DatabaseOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Позиций в справочнике" value={stats.positions}
            prefix={<AppstoreOutlined style={{ color: '#1677ff' }} />} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Складов" value={stats.locations}
            prefix={<ShopOutlined style={{ color: '#52c41a' }} />} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Низкий остаток" value={stats.lowStock}
            prefix={<WarningOutlined style={{ color: stats.lowStock > 0 ? '#ff4d4f' : '#8c8c8c' }} />}
            valueStyle={{ color: stats.lowStock > 0 ? '#ff4d4f' : undefined }} /></Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="stock"
        items={[
          { key: 'stock',     label: 'Остатки',    children: <StockTab /> },
          { key: 'log',       label: 'Журнал',     children: <LogTab /> },
          { key: 'analytics', label: 'Аналитика',  children: <AnalyticsTab /> },
        ]}
      />
    </div>
  );
}